import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  InvestmentInCompaniesAccountModel,
  InvestmentInCompaniesDepositsModel,
  InvestmentInCompaniesModel
} from './investment-incompanies.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const INVESTMENT_ENDPOINT = '/investmentincompanies';
const DEPOSIT_ENDPOINT = '/investmentincompaniesdeposit';
const V2_ENDPOINT = '/api/v2/investment-companies';

@Injectable({
  providedIn: 'root',
})
export class InvestmentInCompaniesService {
  constructor(private http: HttpClient) {}

  public downloadFileImage(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('investmentincompaniesId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/investmentincompanies/file/downloadimage', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'image/png' });
    });
  }

  public downloadFilePDF(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('investmentincompaniesId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/investmentincompanies/file/downloadpdf', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/pdf' });
    });
  }

  public get(uid: string): Promise<InvestmentInCompaniesModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentInCompaniesModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se pudo obtener la inversión.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<InvestmentInCompaniesModel>> {
    let params = new HttpParams();
    if (offset !== undefined && limit !== undefined) {
      params = params.set('offset', offset.toString()).set('limit', limit.toString());
    }

    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ResultModel<InvestmentInCompaniesModel>>>(environment.apiUrl + V2_ENDPOINT, { params })),
      'No se pudieron obtener las inversiones en empresas.'
    );
  }

  public accounts(): Promise<InvestmentInCompaniesAccountModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentInCompaniesAccountModel[]>>(environment.apiUrl + V2_ENDPOINT + '/accounts')),
      'No se pudieron obtener las cuentas contables.'
    ).then(list => list || []);
  }

  public add(investmentincompanies: InvestmentInCompaniesModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, investmentincompanies)
    ).then((response) => response.data);
  }

  public edit(investmentincompanies: InvestmentInCompaniesModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, investmentincompanies)
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('investmentincompaniesId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public depositList(
    investmentincompaniesUid: string
  ): Promise<InvestmentInCompaniesDepositsModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentInCompaniesDepositsModel[]>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(investmentincompaniesUid) + '/deposits')),
      'No se pudieron obtener los pagos de la inversión.'
    ).then(list => list || []);
  }

  public depositCreate(deposit: InvestmentInCompaniesDepositsModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, deposit)
    ).then((response) => response.data);
  }

  public depositUpdate(deposit: InvestmentInCompaniesDepositsModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, deposit)
    ).then((response) => response.data);
  }

  public depositDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('investmentincompaniesdepositId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}
